import {
  Eye,
  CalendarClock,
  PiggyBank,
  Brain,
  Cable,
  LayoutGrid,
} from "lucide-react";
import { CIF } from "@/constants/testIds";

const CARDS = [
  {
    icon: Eye,
    title: "Know Your Total Cost",
    desc: "Replace FOB-only thinking with a complete, defensible landed cost across factory, freight and insurance.",
  },
  {
    icon: CalendarClock,
    title: "Plan Before Purchasing",
    desc: "Run scenarios across destinations, container types and quantities before you commit to a Purchase Order.",
  },
  {
    icon: PiggyBank,
    title: "Better Budget Control",
    desc: "Surface cost drift early. Approve POs with a real budget number — not a hopeful estimate.",
  },
  {
    icon: Brain,
    title: "Smarter Procurement Decisions",
    desc: "Compare suppliers on landed cost per MT, not on factory price alone. Pick the deal that actually wins.",
  },
  {
    icon: Cable,
    title: "Connected With FreightIQ™",
    desc: "Live indicative ocean freight rates flow directly into your CIF — no copy-paste, no stale data.",
  },
  {
    icon: LayoutGrid,
    title: "One Workspace",
    desc: "Estimated CIF™ lives next to RFQ™, CommodityBid™, Inspection™ and Tracking™ — one continuous flow.",
  },
];

export const WhyCIF = () => {
  return (
    <section
      id="why"
      data-testid={CIF.why}
      className="relative py-24 lg:py-32 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-bold text-blue-700">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-600" /> Why Estimated CIF™
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] font-medium tracking-[-0.025em] leading-[1.05] text-slate-900 mt-4">
            Procurement intelligence, not a calculator.
          </h2>
          <p className="text-slate-500 mt-5 text-[15px] leading-relaxed">
            Estimated CIF&trade; exists for one job — help buyers commit with confidence. It&rsquo;s a decision tool, designed for the moment between RFQ and Purchase Order.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {CARDS.map((c, i) => (
            <div
              key={c.title}
              data-testid={CIF.whyCard(i + 1)}
              className="group relative rounded-2xl bg-white border border-slate-200 p-7 hover:-translate-y-1 hover:shadow-[0_25px_60px_-30px_rgba(15,23,42,0.25)] transition-all"
            >
              <div className="absolute top-7 right-7 mono text-[10px] tracking-widest uppercase text-slate-300 font-semibold">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 text-white group-hover:bg-blue-600 transition-colors">
                <c.icon className="h-5 w-5" />
              </div>
              <h3 className="font-display text-xl font-medium tracking-tight text-slate-900 mt-6">
                {c.title}
              </h3>
              <p className="mt-3 text-[14px] text-slate-500 leading-relaxed">
                {c.desc}
              </p>
              <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between">
                <span className="text-[11px] text-slate-400">Workspace capability</span>
                <span className="mono text-[10px] tracking-widest uppercase text-blue-700 font-semibold">CIF™</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyCIF;
